import { namespace } from '@common-config/constants';
import { appConfig } from '@common/config/app.config';
import type { AiChatStream } from '@common-components/ai-chat/AiChat';
import { DEFAULT_ATTACHMENT_PROMPT, UploadedAttachment } from '@common-components/ai-chat/attachments';
import { AiChatItem, BuilderAsk, toItems } from '@common-components/ai-chat/transcript';
import { withAskText } from './builder-ask';
import { stepTools, useAiStream } from '@common-components/ai-chat/useAiStream';
import { uploadAiChatAttachment } from '@common/services/ai-chat-upload';
import { applyAuthHeader } from '@common/reducers/backend-api/apply-auth-header';
import { backendApi, MyBridgeAgent } from '@common/reducers/backend-api/backend-api';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportAgentEvent } from '@common/services/agent-analytics';

/**
 * The agent-builder conversation as data — ONE hook for creating a new agent and
 * for editing an existing one. No markup: the chat view renders what it returns.
 *
 *  - create (no `agentId`): each message is a build turn (`startBuild`, resumed
 *    across ask→answer); the conversation lives here until the agent exists.
 *  - edit (`agentId`): each message is an edit turn on that agent; the
 *    conversation is the agent's builder transcript from the server.
 *
 * Session isolation (F6): every open Edit chat is its OWN builder conversation.
 * The first message sends no `conversationId` (the backend starts a fresh one and
 * returns its id); every follow-up in the same open chat sends that id back. An
 * earlier conversation is never continued and never shown: the server transcript
 * is shown only when it IS the open conversation. Opening the editor again (a new
 * hook) or `reset` ("New chat") starts fresh. Two exceptions carry a conversation
 * into the open chat: the one this chat just created the agent in (create → edit),
 * and a turn still running when the chat opens (reconnect after a refresh).
 *
 * Create → edit continuity: when a build creates the agent the hook calls
 * `onCreated` (the workspace then points itself at the new agent, URL replace)
 * and appends a "Ready" item. When `agentId` then becomes that agent, the
 * on-screen conversation stays: the local turns show until the server
 * transcript arrives, and the Ready item stays until the next message.
 *
 * Both modes run as an async job the hook polls (the builder can take minutes).
 *
 * Live view (rev33 + rev 3.3): while a job runs, the hook ALWAYS reads the job's SSE
 * stream (`builderJobStreamUrl`) through the shared `useAiStream` and returns it as
 * `stream`: the backend phases, thinking, tool steps, and the text as it is written.
 * Every detail level streams — the level ("Show while it works") only filters what
 * the chat renders, Quiet included (it still needs the phases). The POLL stays the
 * only source of truth. The stream writes nothing, and a `done` only makes the hook
 * re-read the job at once. When the job settles, the saved conversation replaces the
 * live turn. An `error` (the 10-minute cap, a dropped connection, a 404 when the
 * backend has no live view) keeps the failed stream: the chat's status line says the
 * connection went quiet until the poll settles; the stream is never retried. The
 * 10-minute cap sends `error{code:'pending', conversationId}` (the job's builder chat
 * key): the chat says "past the time limit" and the poll — still running — brings the
 * answer; the id itself is not needed here (the hook already knows its job).
 * Two tabs may each stream the same job: the stream is read-only.
 */

/** The id of the "Ready" item — the view renders the created agent's card for it. */
export const READY_ITEM_ID = 'builder-ready';

export interface BuilderChatContext {
  chatId: string;
  chatName?: string;
}

export interface UseBuilderChatOptions {
  /** The agent to edit; absent/null = create a new one. */
  agentId?: string | null;
  /** Bind a NEW agent to one WhatsApp chat (the Quick-Agent case). */
  chatContext?: BuilderChatContext;
  /** The account can't build (not provisioned / feature off). */
  disabled?: boolean;
  /** A build created the agent. */
  onCreated?: (agent: MyBridgeAgent) => void;
}

export interface BuilderChat {
  /**
   * Everything the chat shows, in order: the conversation, then the message being
   * sent (edit), a notice, an error (an assistant row with `error`), and the
   * "Ready" item after a create (`READY_ITEM_ID`).
   */
  items: AiChatItem[];
  working: boolean;
  /** The running turn, live (null = no stream yet: the chat's status line says "Sending…"). */
  stream: AiChatStream | null;
  /** The builder can't take messages (feature off / not configured). */
  unavailable: boolean;
  /** The agent the last build created (the Ready item's agent). */
  readyAgent: MyBridgeAgent | null;
  /** Resolves false when the message was not sent (the view keeps the draft). Text may be
   *  empty only when `attachments` is non-empty (AiChat already enforces this). */
  send: (text: string, attachments?: UploadedAttachment[]) => Promise<boolean>;
  /** Forget the conversation: the next message starts a fresh build (create) or a new Edit conversation (edit). */
  reset: () => void;
  /**
   * Stop the running turn (create or edit alike): tells the backend, drops the
   * live view, and ends busy with a neutral "Stopped" row — not an error. A
   * no-op when nothing is running.
   */
  stop: () => Promise<void>;
  /** Wired straight into `<AiChat attachments={{ upload }}>`. */
  upload: (file: File) => Promise<UploadedAttachment>;
  /**
   * #813: the agent's EARLIER builder conversation, offered read-only while the open chat is
   * still empty (F6 keeps it out of `items`). `resume()` is the user's opt-in: it makes that
   * conversation the open one, so it shows and the next message continues it. Null = nothing to offer.
   */
  previous: { items: AiChatItem[]; groups: { conversationId: string; items: AiChatItem[] }[]; resume: () => void } | null;
}

const POLL_MS = 2000;
/** The longest a settled create turn waits for the new agent's transcript. */
const SETTLE_MAX_MS = 8000;
/**
 * How long the job poll may keep failing (a 503 while the builder is not ready, the
 * network) before the turn ends with an error instead of spinning forever. Any
 * successful read restarts the clock.
 */
export const POLL_GIVE_UP_MS = 2 * 60_000;

/** The `BT-XXXXXX` support reference a failed backend call carries in its message, if any. */
export function supportReference(error: unknown): string | null {
  const message = (error as { data?: { message?: unknown } } | undefined)?.data?.message;
  return typeof message === 'string' ? (/Reference:\s*(BT-[A-Z0-9]+)/.exec(message)?.[1] ?? null) : null;
}

/** The live view of one build / edit job (owner JWT; the same owner-scoped read as the poll). */
export function builderJobStreamUrl(jobId: string): string {
  return `${appConfig.API_ENDPOINT}/my-bridge/agents/build/jobs/${encodeURIComponent(jobId)}/stream`;
}

/** The on-screen status rows after the conversation (all optional). */
export interface BuilderChatTail {
  pending?: string | null;
  /** The attachment chips on the `pending` edit-turn row, if any were sent with it. */
  pendingAttachments?: { name: string; mime: string }[] | null;
  notice?: string | null;
  error?: string | null;
  ready?: AiChatItem | null;
}

/**
 * What the chat shows. `server` = the edit-mode transcript (null while loading);
 * `local` = the create-mode conversation, kept on screen after the agent exists
 * until the server transcript has something to show.
 */
export function visibleItems(local: AiChatItem[], server: AiChatItem[] | null, tail: BuilderChatTail = {}): AiChatItem[] {
  const out: AiChatItem[] = [...(server && server.length > 0 ? server : local)];
  if (tail.pending) {
    out.push({
      id: 'builder-pending',
      kind: 'user',
      text: tail.pending,
      ...(tail.pendingAttachments?.length ? { attachments: tail.pendingAttachments } : {}),
    });
  }
  if (tail.ready) out.push(tail.ready);
  if (tail.notice) out.push({ id: 'builder-notice', kind: 'assistant', text: tail.notice });
  if (tail.error) out.push({ id: 'builder-error', kind: 'assistant', text: tail.error, error: true });
  return out;
}

/**
 * F6: may the chat show this server transcript? Only when it is the open
 * conversation. A backend that does not report `conversationId` (absent, not null)
 * still continues one per-agent conversation, so its transcript shows as before.
 */
export function isOpenConversation(transcriptConversationId: string | null | undefined, open: string | null): boolean {
  if (transcriptConversationId === undefined) return true;
  return !!open && transcriptConversationId === open;
}

/** The first build turn tells the builder which chat the agent serves, so it never asks. */
export function withChatBinding(raw: string, chatContext: BuilderChatContext | undefined, resuming: boolean): string {
  if (resuming || !chatContext?.chatId) return raw;
  const { chatId, chatName } = chatContext;
  return `${raw}\n\n[System note: this agent serves ONLY the chat ${chatId}${chatName ? ` (${chatName})` : ''}. Set chatIds to ["${chatId}"] and includeNewChats false. Do NOT ask which chats — it is this one.]`;
}

export function useBuilderChat({ agentId, chatContext, disabled, onCreated }: UseBuilderChatOptions): BuilderChat {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.UserReducer?.accessToken);
  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;

  // The agent builder was opened (new or edit) — a durable product event, once per
  // mount. No chatId is sent from the client (not in scope; server-only).
  useEffect(() => {
    reportAgentEvent('agent_builder_opened', 'claude', { agentId: agentId ?? undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [startBuild, { isLoading: isStarting }] = backendApi.useStartMyBridgeAgentBuildMutation();
  const [sendEdit, { isLoading: isSendingEdit }] = backendApi.useSendMyBridgeAgentBuilderChatMutation();
  const [stopJob] = backendApi.useStopMyBridgeAgentBuildJobMutation();
  const { currentData: transcript, refetch } = backendApi.useGetMyBridgeAgentBuilderChatQuery(
    agentId ? { agentId } : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  // Edit mode (F6): the open chat's builder conversation; null = the next message starts a new one.
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Create-mode conversation + the build session it resumes.
  const [local, setLocal] = useState<AiChatItem[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const nextLocalId = useRef(0);
  /** Appends create-mode rows; returns the id of the last one. */
  const pushLocal = (
    ...rows: (
      | { kind: 'user'; text: string; attachments?: { name: string; mime: string }[] }
      | { kind: 'assistant'; text: string; ask?: BuilderAsk }
      | { kind: 'tool'; name: string }
    )[]
  ) => {
    const added = rows.map((row) => ({ ...row, id: `l${nextLocalId.current++}` }) as AiChatItem);
    setLocal((items) => [...items, ...added]);
    return added[added.length - 1]?.id;
  };
  // The agent this conversation created: its conversation is carried into edit
  // mode. `readyAgent` (the Ready card) is shown only until the next message.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [readyAgent, setReadyAgent] = useState<MyBridgeAgent | null>(null);

  const [pending, setPending] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<{ name: string; mime: string }[] | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  // A job has settled and the saved conversation is on its way (edit: the transcript
  // refetch; create: the new agent's transcript, once the host re-points us at it).
  // Until it can replace them, the sent message and the live turn stay on screen.
  const [settling, setSettling] = useState(false);
  const jobKind = useRef<'create' | 'edit'>('create');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Jobs already handled — the transcript's `runningJobId` still names one until
  // its refetch lands, and it must not be picked up again.
  const doneJobs = useRef(new Set<string>());

  // Pointed at ANOTHER agent (not the one this conversation created): start clean.
  const carried = !agentId || agentId === createdId;
  useEffect(() => {
    if (!carried) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carried]);

  // Reconnect to an edit turn still running (e.g. after a refresh).
  useEffect(() => {
    const running = transcript?.runningJobId;
    if (running && !jobId && !doneJobs.current.has(running)) {
      jobKind.current = 'edit';
      setJobId(running);
      // The running turn belongs to the agent's current conversation: this chat continues it.
      if (transcript?.conversationId) setConversationId(transcript.conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript?.runningJobId, jobId]);

  // `currentData` (not `data`): specific to THIS jobId, so a previous turn's
  // finished job can never end the new turn's polling early.
  const { currentData: job, error: jobError, refetch: refetchJob } = backendApi.useGetMyBridgeAgentBuildJobQuery(jobId ?? skipToken, {
    pollingInterval: POLL_MS,
  });
  // The poll keeps failing: give the turn up after POLL_GIVE_UP_MS of unbroken errors,
  // and stop the server job (best effort) so it can't land a late edit or race a retry.
  const pollFailingSince = useRef<number | null>(null);
  useEffect(() => {
    if (!jobId || !jobError) {
      pollFailingSince.current = null;
      return;
    }
    pollFailingSince.current ??= Date.now();
    const timer = setTimeout(() => {
      stopJob({ jobId }).unwrap().catch(() => undefined);
      doneJobs.current.add(jobId);
      setJobId(null);
      setPending(null);
      setPendingAttachments(null);
      const ref = supportReference(jobError);
      setError(ref ? t('myBridge.builder.pollFailedRef', { ref }) : t('myBridge.builder.pollFailed'));
    }, pollFailingSince.current + POLL_GIVE_UP_MS - Date.now());
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, jobError]);
  // Read the job NOW, not at the next 2 s tick: after the stream's `done`, and when
  // a hidden tab (whose timers the browser throttles) becomes visible again.
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  const refetchJobRef = useRef(refetchJob);
  refetchJobRef.current = refetchJob;
  const readJobNow = () => {
    if (!jobIdRef.current) return;
    try {
      void refetchJobRef.current();
    } catch {
      /* not subscribed yet: the poll will read it */
    }
  };
  useEffect(() => {
    if (!jobId || typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') readJobNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // The live view: opened per job, dropped when the saved conversation takes over.
  // What the builder saves is the Claude `result`: the text after its last tool
  // call (verified on staging 2026-09-27). Text written before a tool call comes
  // through as narration steps: the chat shows them as quiet lines while the turn
  // runs, and the saved reply replaces the whole live turn (the btgpt rule too).
  const live = useAiStream(undefined, { onDone: readJobNow });
  useEffect(() => {
    if (!jobId) return;
    const streamJob = jobId;
    void live.start(async (signal) => {
      const headers = new Headers({ Accept: 'text/event-stream' });
      await applyAuthHeader(headers, tokenRef.current, async () => {
        const { authService } = await import('@/common/bt-auth/auth-service');
        return authService.getPersistedToken();
      });
      return fetch(builderJobStreamUrl(streamJob), { headers, signal });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  const liveVisible = !!jobId || settling;
  useEffect(() => {
    if (!liveVisible) live.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVisible]);

  useEffect(() => {
    if (!jobId || !job || job.status === 'running') return;
    doneJobs.current.add(jobId);
    setJobId(null);
    const result = job.result;
    // The builder may have changed or created an agent: refetch overview/policy/schedules.
    // MCP servers have their own tag (#336): the builder attaches integrations, and the
    // Integrations list + the chat's Connect card read that cache, so it must refetch too.
    dispatch(backendApi.util.invalidateTags(['MyBridge', 'MyBridgeMcpServers']));

    if (jobKind.current === 'edit') {
      if (result?.kind === 'error') setError(result.message);
      else if (result && 'message' in result && result.message) setNotice(result.message);
      if (!agentId) {
        setPending(null);
        setPendingAttachments(null);
        return;
      }
      setSettling(true);
      void Promise.resolve(refetch())
        .catch(() => undefined)
        .then(() => {
          setPending(null);
          setPendingAttachments(null);
          setSettling(false);
        });
      return;
    }

    if (job.status === 'unknown' || !result) {
      // The job is gone (expired, or the runtime restarted mid-build). The agent
      // may already have been created — say so honestly.
      setError(t('myBridge.builder.interrupted'));
      return;
    }
    if (result.kind === 'created') {
      setSettling(true);
      setSessionId(undefined);
      // The same open chat goes on editing the agent it created: its next turns continue
      // the creating conversation (the build's chat key, linked on the new agent).
      setConversationId(result.sessionId ?? null);
      setCreatedId(result.agent.id);
      setReadyAgent(result.agent);
      // The authoritative creation point for a chat-built Claude agent — fires exactly
      // once here. This does NOT double with AgentForm's agent_created: the builder
      // creates via the build job (never AgentForm.createAgent), and once created the
      // workspace re-points at the new agent so any later AgentForm save is an UPDATE
      // (agentId set → agent_updated). The two create routes are mutually exclusive.
      reportAgentEvent('agent_created', 'claude', { agentId: result.agent.id, model: result.agent.model });
      onCreated?.(result.agent);
      return;
    }
    if (result.kind === 'ask') {
      setSessionId(result.sessionId);
      // Nothing is saved server-side for a create turn yet: keep the tool steps the
      // live view showed (their names only), then the reply — as the edit transcript will.
      // #813 step 4: confirm cards the builder raised this turn (update the existing agent / create a new one).
      const cards = (result.asks ?? []).map((ask) => ({ kind: 'assistant' as const, text: '', ask }));
      pushLocal(...stepTools(live.steps).map((tool) => ({ kind: 'tool' as const, name: tool.name })), { kind: 'assistant', text: result.reply }, ...cards);
      return;
    }
    if (result.kind === 'stopped') {
      // Keep the session so the next message resumes it (same as `ask`); the
      // row is a plain assistant turn — neutral, never an error.
      setSessionId(result.sessionId);
      pushLocal(...stepTools(live.steps).map((tool) => ({ kind: 'tool' as const, name: tool.name })), { kind: 'assistant', text: result.message });
      return;
    }
    if (result.kind === 'updated') return; // an edit outcome never answers a create turn
    if (result.sessionId) setSessionId(result.sessionId);
    setError(result.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, jobId]);

  // Create: the new agent's saved conversation has landed (or never will: a host
  // that does not re-point, a failed read) — the live turn gives way to it.
  useEffect(() => {
    if (!settling || jobKind.current !== 'create') return;
    if (transcript) {
      setSettling(false);
      return;
    }
    const timer = setTimeout(() => setSettling(false), SETTLE_MAX_MS);
    return () => clearTimeout(timer);
  }, [settling, transcript]);

  const send = async (raw: string, attachments?: UploadedAttachment[]): Promise<boolean> => {
    const text = raw.trim();
    const hasAttachments = !!attachments && attachments.length > 0;
    // The backend allows an empty prompt only when attachments ride along with it.
    if ((!text && !hasAttachments) || jobId || settling || isSendingEdit || isStarting || disabled) return false;
    // Fix round 1: an attachment-only turn needs SOME user intent text for the
    // builder to reason over (and for `withChatBinding` to attach its note to) —
    // the same default the backend substitutes for an attachment-only btgpt turn.
    const effective = text || (hasAttachments ? DEFAULT_ATTACHMENT_PROMPT : '');
    const ids = hasAttachments ? attachments!.map((a) => a.uploadId) : undefined;
    const attachmentMeta = hasAttachments ? attachments!.map((a) => ({ name: a.name, mime: a.mime })) : undefined;
    setError(null);
    setNotice(null);
    if (agentId) {
      setReadyAgent(null); // the Ready card belongs to the moment of creation only
      setPending(effective);
      setPendingAttachments(attachmentMeta ?? null);
      try {
        const res = await sendEdit({
          agentId,
          prompt: effective,
          attachments: ids,
          ...(conversationId ? { conversationId } : {}),
        }).unwrap();
        if (res.conversationId) setConversationId(res.conversationId);
        jobKind.current = 'edit';
        setJobId(res.jobId);
        reportAgentEvent('agent_builder_message', 'claude', { agentId, kind: 'chat' });
        return true;
      } catch {
        setPending(null);
        setPendingAttachments(null);
        setError(t('myBridge.builderChat.sendFailed'));
        return false;
      }
    }
    const userRow = pushLocal({ kind: 'user', text: effective, ...(attachmentMeta ? { attachments: attachmentMeta } : {}) });
    try {
      const res = await startBuild({
        prompt: withChatBinding(effective, chatContext, !!sessionId),
        resumeSessionId: sessionId,
        attachments: ids,
      }).unwrap();
      jobKind.current = 'create';
      setJobId(res.jobId);
      reportAgentEvent('agent_builder_message', 'claude', { agentId: agentId ?? undefined, kind: 'chat' });
      return true;
    } catch {
      // Same as an edit: the message goes back into the composer, the error says why.
      setLocal((items) => items.filter((it) => it.id !== userRow));
      setError(t('myBridge.builder.startFailed'));
      return false;
    }
  };

  const stop = useCallback(async () => {
    const runningJobId = jobId;
    if (!runningJobId) return;
    const callStop = () => stopJob({ jobId: runningJobId }).unwrap();
    try {
      const res = await callStop();
      // The one edge case the contract documents: the input hadn't landed on the
      // session yet (within ms of the start) — retry once, shortly after.
      if (res.status === 'running') {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await callStop();
      }
    } catch {
      /* best effort — the next poll still reflects the job's real status */
    }
    // Don't touch local state here: the job's status is now terminal
    // (done/error/stopped) server-side, so the normal poll — nudged now
    // instead of waiting up to POLL_MS — renders the outcome exactly like any
    // other finished turn (the `result.kind === 'stopped'` branches above).
    readJobNow();
  }, [jobId, stopJob]);

  const upload = useCallback((file: File) => uploadAiChatAttachment(file, tokenRef.current), []);

  function reset() {
    setConversationId(null);
    setLocal([]);
    setSessionId(undefined);
    setCreatedId(null);
    setReadyAgent(null);
    setPending(null);
    setPendingAttachments(null);
    setError(null);
    setNotice(null);
    setJobId(null);
    setSettling(false);
  }

  const server = useMemo(
    () =>
      agentId && transcript && isOpenConversation(transcript.conversationId, conversationId)
        ? toItems(transcript.turns ?? [])
        : null,
    [agentId, transcript, conversationId],
  );
  const previous = useMemo(() => {
    const earlierId = transcript?.conversationId;
    const turns = transcript?.turns ?? [];
    if (!agentId || !earlierId || turns.length === 0) return null;
    if (conversationId !== null || jobId || pending || settling) return null;
    // An agent's history spans several conversations (create chat, each edit chat): group the
    // turns per conversation, in first-seen (oldest first) order, so none is lost or merged (#813).
    const order: string[] = [];
    const byConv = new Map<string, typeof turns>();
    for (const turn of turns) {
      const key = turn.conversationId ?? earlierId;
      const bucket = byConv.get(key);
      if (bucket) bucket.push(turn);
      else { byConv.set(key, [turn]); order.push(key); }
    }
    const groups = order.map((id) => ({ conversationId: id, items: toItems(byConv.get(id) ?? []) }));
    return { items: toItems(turns), groups, resume: () => setConversationId(earlierId) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, transcript, conversationId, jobId, pending, settling]);
  const readyItem: AiChatItem | null = useMemo(
    () => (readyAgent ? { id: READY_ITEM_ID, kind: 'assistant', text: t('myBridge.workspace.readyTitle', { name: readyAgent.label }) } : null),
    [readyAgent, t],
  );
  const items = useMemo(
    () => visibleItems(withAskText(carried ? local : [], t), server ? withAskText(server, t) : server, { pending, pendingAttachments, notice, error, ready: settling ? null : readyItem }),
    [carried, local, server, pending, pendingAttachments, notice, error, readyItem, settling, t],
  );

  // A `done` whose job has not been re-read yet stays on screen (the chat paints a
  // `done` turn while `busy`, without the caret). A failed stream stays too: the
  // status line then says the connection went quiet, until the poll settles.
  const stream: AiChatStream | null =
    liveVisible && live.phase !== 'idle'
      ? {
          phase: live.phase,
          infra: live.infra,
          textSoFar: live.textSoFar,
          steps: live.steps,
          log: live.log,
          error: live.error,
          failedFrom: live.failedFrom,
          startedAt: live.startedAt,
          stepStartedAt: live.stepStartedAt,
          lastByteAt: live.lastByteAt,
          thinkingTokens: live.thinkingTokens,
        }
      : null;

  return {
    items,
    working: !!jobId || settling || isSendingEdit || isStarting,
    stream,
    unavailable: !!disabled || transcript?.available === false,
    readyAgent,
    send,
    reset,
    stop,
    upload,
    previous,
  };
}
